import React, { createContext, useState, useContext, ReactNode } from "react";

type FormKey = "singleForm" | "requestForm" | "totalOfferPrice";

type ValidationKey = "singleForm" | "requestForm";

interface DynamicFormContextType {
  dynamicFormValues: Record<FormKey, any>;
  setDynamicFormValues: (key: FormKey, value: any) => void;
  setSingleFormValues: (itemId: string, value: any) => void;
  setRequestFormValue: (key: string, value: any) => void;
  resetRequestFormValue: (key: string) => void;
  resetSingleFormValues: () => void;
  isDynamicFormValid: boolean;
  setIsDynamicFormValid: React.Dispatch<React.SetStateAction<boolean>>;
  setDynamicFormValidations: (key: ValidationKey, value: any) => void;
  setSingleRequestFormValue: () => void;
  isDirtyForm: boolean;
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
}

const DynamicFormContext = createContext<DynamicFormContextType | undefined>(
  undefined
);

export const DynamicFormProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [dynamicFormValues, setDynamicFormValuesState] = useState<
    Record<FormKey, any>
  >({
    singleForm: {},
    requestForm: {},
    totalOfferPrice: null,
  });

  const [isDynamicFormValid, setIsDynamicFormValid] = useState<
    Record<ValidationKey, any>
  >({
    singleForm: {},
    requestForm: {},
  });

  const setDynamicFormValidations = (key: ValidationKey, value: any) => {
    setIsDynamicFormValid((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const [isDirtyForm, setIsDirty] = useState(false);

  const setDynamicFormValues = (key: FormKey, value: any) => {
    setDynamicFormValuesState((prev) => ({
      ...prev,
      [key]:
        key === "requestForm" && prev.requestForm?.account_id && value
          ? { account_id: prev.requestForm.account_id, ...value }
          : value,
    }));
  };

  const setSingleFormValues = (itemId: string, value: any) => {
    setDynamicFormValuesState((prev) => ({
      ...prev,
      singleForm: {
        ...prev.singleForm,
        [itemId]: value,
      },
    }));
  };
  const resetSingleFormValues = () => {
    setDynamicFormValuesState((prev) => ({
      ...prev,
      singleForm: {},
    }));
  };
  const setRequestFormValue = (key: string, value: any) => {
    setDynamicFormValuesState((prev) => ({
      ...prev,
      requestForm: {
        ...prev.requestForm,
        [key]: value,
      },
    }));
  };
  const resetRequestFormValue = (key: string) => {
    setDynamicFormValuesState((prev) => ({
      ...prev,
      requestForm: {
        ...prev.requestForm,
        [key]: "",
      },
    }));
  };
  const setSingleRequestFormValue = (
    itemId: string,
    key: string,
    value: any
  ) => {
    setDynamicFormValuesState((prev) => ({
      ...prev,
      singleForm: {
        ...prev.singleForm,
        [itemId]: {
          ...prev.singleForm?.[itemId],
          [key]: value,
        },
      },
    }));
  };

  const value = React.useMemo(
    () => ({
      dynamicFormValues,
      setDynamicFormValues,
      setSingleFormValues,
      resetSingleFormValues,
      isDynamicFormValid,
      setIsDynamicFormValid,
      setDynamicFormValidations,
      setRequestFormValue,
      resetRequestFormValue,
      setSingleRequestFormValue,
      isDirtyForm,
      setIsDirty,
    }),
    [dynamicFormValues, isDynamicFormValid]
  );
  return (
    <DynamicFormContext.Provider value={value}>
      {children}
    </DynamicFormContext.Provider>
  );
};

export const useDynamicFormState = () => {
  const context = useContext(DynamicFormContext);
  if (context === undefined) {
    throw new Error(
      "useDynamicFormState must be used within a DynamicFormProvider"
    );
  }
  return context;
};
